import React, { useRef, useState } from "react";
import { Upload, ImagePlus } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

const MAX_FILES = 15;
const MAX_SIZE = 50 * 1024 * 1024;

const ImageDropzone = ({ onUploaded, testId = "image-dropzone" }) => {
    const inputRef = useRef(null);
    const [drag, setDrag] = useState(false);
    const [progress, setProgress] = useState(null);

    const handleFiles = async (fileList) => {
        const files = Array.from(fileList || []).filter((f) => f.type.startsWith("image/"));
        if (files.length === 0) { toast.error("Görsel dosyası seçilmedi"); return; }
        if (files.length > MAX_FILES) { toast.error(`Tek seferde en fazla ${MAX_FILES} görsel yükleyebilirsiniz`); return; }
        const oversized = files.filter((f) => f.size > MAX_SIZE);
        if (oversized.length) { toast.error(`50MB üzeri dosyalar: ${oversized.map((f) => f.name).join(", ")}`); return; }
        const fd = new FormData();
        files.forEach((f) => fd.append("files", f));
        setProgress(0);
        try {
            const { data } = await api.post("/admin/upload-images", fd, {
                headers: { "Content-Type": "multipart/form-data" },
                onUploadProgress: (e) => setProgress(e.total ? Math.round((e.loaded / e.total) * 100) : 0),
            });
            const urls = (data.uploaded || []).map((u) => u.url);
            if (urls.length) {
                onUploaded(urls);
                toast.success(`${urls.length} görsel yüklendi — kaydet'i unutmayın`);
            }
            (data.errors || []).forEach((er) => toast.error(`${er.filename}: ${er.detail}`));
        } catch (err) {
            toast.error(err.response?.data?.detail || "Yükleme başarısız");
        } finally {
            setProgress(null);
            if (inputRef.current) inputRef.current.value = "";
        }
    };

    return (
        <div
            onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
            onDragLeave={() => setDrag(false)}
            onDrop={(e) => { e.preventDefault(); setDrag(false); handleFiles(e.dataTransfer.files); }}
            onClick={() => progress === null && inputRef.current?.click()}
            className={`border-2 border-dashed px-4 py-6 text-center cursor-pointer transition-colors select-none ${
                drag ? "border-gold bg-gold/10" : "border-border_green hover:border-gold/60"
            }`}
            data-testid={testId}
        >
            <input
                ref={inputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                multiple
                className="hidden"
                onChange={(e) => handleFiles(e.target.files)}
                data-testid={`${testId}-input`}
            />
            {progress !== null ? (
                <div className="space-y-2" data-testid={`${testId}-progress`}>
                    <div className="text-xs text-gold">Yükleniyor… %{progress}</div>
                    <div className="h-1.5 bg-emerald_deep max-w-xs mx-auto">
                        <div className="h-full bg-gold transition-all" style={{ width: `${progress}%` }} />
                    </div>
                </div>
            ) : (
                <div className="flex flex-col items-center gap-1.5">
                    <div className="flex items-center gap-2 text-gold">
                        <ImagePlus className="w-4 h-4" />
                        <span className="text-xs uppercase tracking-[0.15em] font-semibold">Görselleri sürükleyin veya tıklayın</span>
                    </div>
                    <div className="text-[10px] text-muted_green flex items-center gap-1">
                        <Upload className="w-3 h-3" /> Çoklu seçim desteklenir · max {MAX_FILES} dosya · her biri max 50MB
                    </div>
                </div>
            )}
        </div>
    );
};

export default ImageDropzone;
